import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import DealerGroup from 'models/dealerManagement/dealerGroup/Model.DealerGroup';
import StatementSummary from 'models/sales/statementSummary/Model.StatementSummary';
import statementSummaryCreateForDealerGroupApi from 'models/sales/statementSummary/createForDealerGroup/Api.StatementSummary.CreateForDealerGroup';
import statementSummaryDeleteApi from 'models/sales/statementSummary/delete/Api.StatementSummary.Delete';
import { useMemo } from 'react';
import { statementSummaryIndexRoute } from './StatementSummary.Index';

export enum StatementSummaryAction {
  CREATE = 'create',
  PRINT = 'print',
  DELETE = 'delete',
}
export type VerifyStatementSummaryAction = Utils.VerifyExtends<
  Module.Claims.StatementSummary.Actions,
  Utils.ValueOf<typeof StatementSummaryAction>
>;
export default function useStatementSummary() {
  const { user, permissions, isDealer } = useAuthStore();
  const pageRouter = usePageRouter<Module.Claims.StatementSummary.Params, Module.Claims.StatementSummary.Query>({
    route: statementSummaryIndexRoute,
  });

  const dealerGroupListApi = useApi({
    action: DealerGroup.list,
    body: {
      distributorId: user.distributorId ? [user.distributorId] : undefined,
    },
  });
  const dealerGroups = useMemo(
    () =>
      dealerGroupListApi?.payload?.data?.filter((group) => {
        const groupDealerIds = group.dealers?.map((d) => d.id) ?? [];
        const userDealerIds = user?.dealers?.map((d) => d.id) ?? [];
        if (!isDealer || (groupDealerIds.length === 1 && userDealerIds.includes(groupDealerIds[0]))) {
          return true;
        }
        return groupDealerIds.every((dealerId) => userDealerIds.includes(dealerId));
      }),
    [dealerGroupListApi?.payload]
  );

  const canView = permissions.CREATE_STATEMENTSUMMARY || permissions.DELETE_STATEMENTSUMMARY;
  const listApi = useApi(
    {
      action: StatementSummary.list,
      default: {
        limit: defaultLimit,
        offset: defaultOffset,
        distributorId: user?.distributorId ? [user.distributorId] : undefined,
        dealerGroupId: isDealer ? dealerGroups?.map((it) => it.id) : undefined,
      },
      body: {
        ...pageRouter.query.list,
      },
      callback: (req) => pageRouter.redirect({}, { ...pageRouter.query, list: req }),
      wait: !canView || !dealerGroups,
    },
    [dealerGroups]
  );

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const deleteApi = useApi({
    action: statementSummaryDeleteApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const createApi = useApi({
    action: statementSummaryCreateForDealerGroupApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  return {
    canView,
    pageRouter,
    listApi,
    dealerGroupListApi,
    dealerGroups,
    statementSummaries: isDealer
      ? listApi.payload?.data?.filter((ss) => dealerGroups?.some((group) => ss?.dealerGroupId === group?.id))
      : listApi?.payload?.data,
    deleteApi,
    createApi,
    selected,
    permissions,
    views: {
      [StatementSummaryAction.CREATE]:
        pageRouter.params.action === StatementSummaryAction.CREATE && permissions.CREATE_STATEMENTSUMMARY && !selected,
      [StatementSummaryAction.DELETE]:
        pageRouter.params.action === StatementSummaryAction.DELETE && permissions.DELETE_STATEMENTSUMMARY && selected,
      [StatementSummaryAction.PRINT]:
        pageRouter.params.action === StatementSummaryAction.PRINT && permissions.CREATE_STATEMENTSUMMARY && selected,
    },
  };
}

export function useStatementSummaryProvider() {
  return useDataProvider<ReturnType<typeof useStatementSummary>>();
}
